import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { gbp } from "../lib/format";
import { withdraw as withdrawApi } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { Banknote } from "lucide-react";

export const WithdrawModal = ({ open, onOpenChange, wallet, onSuccess }) => {
  const [amount, setAmount] = useState(1000);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    const user = getUser();
    if (!user) return;
    if (amount <= 0) return toast.error("Enter a positive amount.");
    if (amount > wallet) return toast.error("Insufficient wallet balance.");
    setLoading(true);
    try {
      const res = await withdrawApi(user.id, amount);
      saveUser({ ...user, wallet_gbp: res.wallet_gbp });
      toast.success(`${gbp(amount)} sent to your registered bank account.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-slate-200 bg-white p-8" data-testid="withdraw-modal">
        <DialogHeader>
          <div className="overline">Withdraw</div>
          <DialogTitle className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Send funds to your bank
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Same-day settlement to your registered UK bank account via Faster Payments.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Banknote className="h-4 w-4 brickstok-gold" /> Wallet balance
            </div>
            <div className="font-display text-lg font-semibold text-slate-900 font-mono-num">{gbp(wallet)}</div>
          </div>
          <div>
            <Label htmlFor="withdraw-amount" className="text-sm font-medium text-slate-700">Amount</Label>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-num">£</span>
              <Input
                id="withdraw-amount"
                type="number"
                min={1}
                max={wallet}
                step={100}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                className="h-11 pl-7 font-mono-num text-lg font-semibold text-slate-900"
                data-testid="withdraw-amount-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex-row gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="withdraw-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || amount <= 0 || amount > wallet}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="withdraw-confirm-btn"
          >
            {loading ? "Processing..." : `Withdraw ${gbp(amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WithdrawModal;
